import { useId, type ReactNode } from 'react'

/**
 * Logos for the places Insuronix reads files from and the apps people sign in
 * with, so a source is recognisable at a glance instead of being a generic
 * cloud or folder icon.
 *
 * Inline SVG, bundled with the app: nothing is fetched from a vendor's CDN
 * (the Content-Security-Policy would block it, and a logo should never be the
 * reason a page is slow). Third-party marks identify the service they belong
 * to and are used for that purpose only. Where there is no public mark to use
 * - a policy admin system platform, SFTP - a neutral tile stands in.
 */

export type BrandName =
  | 's3'
  | 'google_drive'
  | 'sharepoint'
  | 'onedrive'
  | 'microsoft'
  | 'google'
  | 'azure_blob'
  | 'sftp'
  | 'https'
  | 'upload'
  | 'pas'

/** Data source kind (app/services/sources/registry.py) → logo. */
export const SOURCE_LOGOS: Record<string, BrandName> = {
  upload: 'upload',
  pas: 'pas',
  s3: 's3',
  sharepoint: 'sharepoint',
  onedrive: 'onedrive',
  google_drive: 'google_drive',
}

/** How a policy admin system delivers its extract → logo. */
export const METHOD_LOGOS: Record<string, BrandName> = {
  sftp: 'sftp',
  s3: 's3',
  azure_blob: 'azure_blob',
  https: 'https',
}

/** Connection kinds that have a brand of their own (the rest ship SVG assets). */
export const CONNECTION_LOGOS: Record<string, BrandName> = {
  google_oauth: 'google',
  microsoft_oauth: 'microsoft',
}

const tile = (fill: string, children: ReactNode, radius = 9) => (
  <svg viewBox="0 0 40 40" width="100%" height="100%">
    <rect width="40" height="40" rx={radius} fill={fill} />
    {children}
  </svg>
)

const LOGOS: Record<BrandName, (id: string) => ReactNode> = {
  // Amazon S3: the AWS storage-category tile with a bucket.
  s3: (id) => (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <defs>
        <linearGradient id={`${id}-s3`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#1B660F" />
          <stop offset="1" stopColor="#6CAE3E" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="8" fill={`url(#${id}-s3)`} />
      <ellipse cx="20" cy="13" rx="9.5" ry="3.4" fill="none" stroke="#fff" strokeWidth="1.8" />
      <path
        d="M10.5 13l2.6 14.6c.3 1.7 3.2 3 6.9 3s6.6-1.3 6.9-3L29.5 13"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M13.4 21.2c1.8.9 4.1 1.4 6.6 1.4s4.8-.5 6.6-1.4" fill="none" stroke="#fff" strokeWidth="1.4" />
    </svg>
  ),

  google_drive: () => (
    <svg viewBox="0 0 87.3 78" width="100%" height="100%">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
      <path
        d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
        fill="#ea4335"
      />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path
        d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
        fill="#ffba00"
      />
    </svg>
  ),

  sharepoint: () => (
    <svg viewBox="0 0 48 48" width="100%" height="100%">
      <circle cx="27" cy="15" r="12" fill="#036C70" />
      <circle cx="35.5" cy="26" r="10" fill="#1A9BA1" />
      <circle cx="28" cy="36.5" r="8" fill="#37C6D0" />
      <rect x="3" y="12" width="24" height="24" rx="3" fill="#03787C" />
      <path
        d="M11.2 28.7c.9.6 2.2 1 3.4 1 1.4 0 2.2-.6 2.2-1.5 0-.8-.6-1.3-2.1-1.9-2-.7-3.3-1.8-3.3-3.6 0-2.1 1.7-3.6 4.3-3.6 1.3 0 2.3.3 2.9.6l-.6 2c-.5-.3-1.3-.6-2.4-.6-1.3 0-1.9.7-1.9 1.4 0 .9.7 1.3 2.3 1.9 2.1.8 3.1 1.9 3.1 3.6 0 2-1.6 3.8-4.8 3.8-1.4 0-2.7-.4-3.4-.8z"
        fill="#fff"
      />
    </svg>
  ),

  onedrive: () => (
    <svg viewBox="0 0 48 32" width="100%" height="100%">
      <path d="M18.6 9.1a11.5 11.5 0 0 1 17 3.4l-9.2 3.7L15.8 30H8.5A8 8 0 0 1 7 14.2a11.5 11.5 0 0 1 11.6-5.1z" fill="#0364B8" />
      <path d="M35.6 12.5a11.5 11.5 0 0 0-17-3.4 11.4 11.4 0 0 1 7.8 7.1z" fill="#0078D4" />
      <path d="M26.4 16.2 15.8 30h25.4a7.3 7.3 0 0 0 1.9-14.3 11 11 0 0 0-7.5-3.2z" fill="#1490DF" />
      <path d="M41.2 30a7.3 7.3 0 0 0 4.4-13.1L15.8 30z" fill="#28A8EA" />
    </svg>
  ),

  microsoft: () => (
    <svg viewBox="0 0 21 21" width="100%" height="100%">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  ),

  google: () => (
    <svg viewBox="0 0 48 48" width="100%" height="100%">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  ),

  // Azure Blob Storage: Azure blue with the storage-account grid.
  azure_blob: (id) => (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      <defs>
        <linearGradient id={`${id}-az`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#32BEDD" />
          <stop offset="1" stopColor="#0078D4" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="8" fill={`url(#${id}-az)`} />
      <rect x="9" y="11" width="22" height="18" rx="2" fill="#fff" />
      <path d="M9 17.5h22M9 23h22M16.3 17.5V29M23.7 17.5V29" stroke="#0078D4" strokeWidth="1.4" />
      <rect x="9" y="11" width="22" height="6.5" rx="2" fill="#0F5FA8" opacity=".35" />
    </svg>
  ),

  sftp: () =>
    tile(
      '#334155',
      <>
        <rect x="8" y="10" width="24" height="20" rx="3" fill="none" stroke="#fff" strokeWidth="1.8" />
        <path d="M13 17l3.5 3-3.5 3M19 24h7" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </>,
    ),

  https: () =>
    tile(
      '#4F46E5',
      <>
        <circle cx="18" cy="20" r="9" fill="none" stroke="#fff" strokeWidth="1.7" />
        <path d="M9 20h18M18 11c2.6 2.6 2.6 15.4 0 18M18 11c-2.6 2.6-2.6 15.4 0 18" fill="none" stroke="#fff" strokeWidth="1.4" />
        <rect x="24" y="23" width="9" height="7.5" rx="1.5" fill="#fff" />
        <path d="M26 23v-2a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="#fff" strokeWidth="1.6" />
      </>,
    ),

  upload: () =>
    tile(
      '#2563EB',
      <>
        <path d="M13 9h9l6 6v15a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 12 30V10.5A1.5 1.5 0 0 1 13 9z" fill="#fff" opacity=".95" />
        <path d="M22 9v6h6" fill="#BFDBFE" />
        <path d="M20 27v-8M16.5 22l3.5-3.5 3.5 3.5" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </>,
    ),

  // A policy admin system: no single vendor, so a neutral server stack.
  pas: () =>
    tile(
      '#7C3AED',
      <>
        <rect x="10" y="9" width="20" height="6.5" rx="1.5" fill="#fff" />
        <rect x="10" y="17" width="20" height="6.5" rx="1.5" fill="#fff" opacity=".85" />
        <rect x="10" y="25" width="20" height="6.5" rx="1.5" fill="#fff" opacity=".7" />
        <circle cx="26" cy="12.25" r="1.2" fill="#7C3AED" />
        <circle cx="26" cy="20.25" r="1.2" fill="#7C3AED" />
        <circle cx="26" cy="28.25" r="1.2" fill="#7C3AED" />
      </>,
    ),
}

export function BrandLogo({
  name,
  size = 24,
  title,
  className,
}: {
  name: BrandName | string | null | undefined
  size?: number
  /** Accessible name. Omit when a visible label sits next to the logo. */
  title?: string
  className?: string
}) {
  const id = useId().replace(/:/g, '')
  const render = name ? LOGOS[name as BrandName] : undefined
  if (!render) return null
  return (
    <span
      className={className ? `ix-brand-mark ${className}` : 'ix-brand-mark'}
      style={{ display: 'inline-flex', width: size, height: size, flex: 'none', lineHeight: 0 }}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {render(id)}
    </span>
  )
}

/** A neutral initials tile for a named platform with no mark of its own (Guidewire, Duck Creek...). */
export function PlatformMark({ label, size = 24 }: { label: string; size?: number }) {
  const initials =
    label
      .split(/[\s/-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('') || '?'
  return (
    <span
      className="ix-brand-mark"
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flex: 'none',
        borderRadius: Math.round(size * 0.22),
        background: '#EEF2FF',
        color: '#4338CA',
        border: '1px solid #C7D2FE',
        boxSizing: 'border-box',
        fontSize: Math.max(9, Math.round(size * 0.38)),
        fontWeight: 700,
        letterSpacing: '0.02em',
      }}
    >
      {initials}
    </span>
  )
}

export default BrandLogo
